<script setup lang="ts">

  import { useContentApi } from '@/composables/useCmsContentApi'
  import { IbexaContentTypeIdentifier } from '@/enums/IbexaContentTypeIdentifier'
  import FrameBlock from '@/components/FrameBlock.vue'
  import ExpandElement from '@/components/global/ExpandElement.vue'
  import LongformTextBlock from '@/components/LongformTextBlock.vue'
  import Solution from '@/components/Solution.vue'
  import TextImageElement from '@/components/TextImageElement.vue'
  import ThinkTextElement from '@/components/ThinkTextElement.vue'
  import TopSectionElement from '@/components/TopSectionElement.vue'
  import VgsSpinner from '@/components/common/VgsSpinner.vue'
  import ExternalLink from '@/components/global/ExternalLink.vue'
  import FlytTask from '@/components/task/FlytTask.vue'
  import { useQuery } from '@tanstack/vue-query'
  import Conversation from '@/components/Conversation.vue'
  import GlossCollection from '@/components/global/GlossCollection.vue'
  import Presentation from '@/components/presentation/Presentation.vue'
  import Sentence from '@/components/global/Sentence.vue'
  import PronunciationBoxCollection from '@/components/global/PronunciationBoxCollection.vue'

  const props = defineProps<{
    contentId: number
  }>()

  const cmsContentBlocks = useQuery({
    queryKey: ['contentBlocks', props.contentId],
    queryFn: async () => await getAllChildrenElements(props.contentId),
    staleTime: Infinity
  })
  const { getAllChildrenElements } = useContentApi()

  const getComponentName = (contentTypeIdentifier: IbexaContentTypeIdentifier) => {
    switch (contentTypeIdentifier) {
    case IbexaContentTypeIdentifier.vgsExpandElement:
      return ExpandElement
    case IbexaContentTypeIdentifier.vgsTextImageElement:
      return TextImageElement
    case IbexaContentTypeIdentifier.vgsTopSectionElement:
      return TopSectionElement
    case IbexaContentTypeIdentifier.vgsThinkTextElement:
      return ThinkTextElement
    case IbexaContentTypeIdentifier.vgsLongformText:
      return LongformTextBlock
    case IbexaContentTypeIdentifier.vgsFrameBlock:
      return FrameBlock
    case IbexaContentTypeIdentifier.vgsSolution:
      return Solution
    case IbexaContentTypeIdentifier.vgsExternalLink:
      return ExternalLink
    case IbexaContentTypeIdentifier.vgsFlytTask:
      return FlytTask
    case IbexaContentTypeIdentifier.vgsConversation:
      return Conversation
    case  IbexaContentTypeIdentifier.vgsGlossCollection:
      return GlossCollection
    case IbexaContentTypeIdentifier.vgsPresentation:
      return Presentation
    case IbexaContentTypeIdentifier.vgsSentence:
      return Sentence
    case IbexaContentTypeIdentifier.vgsPronunciationBoxCollection:
      return PronunciationBoxCollection
    default:
      console.warn(`No component found for content type identifier: ${contentTypeIdentifier}`)
      return
    }
  }
</script>

<template>
  <VgsSpinner :is-loading="cmsContentBlocks.isLoading.value" />
  <template v-if="!cmsContentBlocks.isLoading.value">
    <template
      v-for="(cmsContentBlock, index) in cmsContentBlocks.data.value"
      :key="index"
    >
      <component
        :is="getComponentName(cmsContentBlock.contentTypeIdentifier) as unknown as IbexaContentTypeIdentifier"
        v-if="cmsContentBlocks.data.value"
        :index="index"
        :element="cmsContentBlock"
        :total-element-count="cmsContentBlocks.data.value.length"
      />
    </template>
  </template>
</template>
